import { useQuery } from '@tanstack/react-query';
import { DataTable, EmptyState, Money, StatusBadge, type Column } from '@riskpay/ui-components';
import type { Settlement } from '@riskpay/types';
import { api } from '../lib/client';

/**
 * Settlements.
 *
 * A settlement row is what a finance team reconciles against the bank
 * statement, so the UTR and the frozen bank-account snapshot are first-class
 * columns rather than detail-view trivia.
 */
export function Settlements() {
  const { data, isLoading } = useQuery({
    queryKey: ['settlements'],
    queryFn: () => api.get<{ data: Settlement[] }>('/settlements'),
  });

  const columns: Column<Settlement>[] = [
    {
      key: 'id',
      header: 'Settlement',
      render: (s) => (
        <div>
          <p className="numeral text-xs text-content">{s.id}</p>
          <p className="mt-0.5 text-2xs text-content-faint">
            {s.cycle} · {s.transaction_count} transactions
          </p>
        </div>
      ),
    },
    { key: 'gross', header: 'Gross', align: 'right', render: (s) => <Money amount={s.gross.amount} className="text-xs" /> },
    { key: 'fees', header: 'Fees', align: 'right', render: (s) => <Money amount={s.fees.amount} className="text-xs text-content-subtle" /> },
    {
      key: 'reserve',
      header: 'Reserve',
      align: 'right',
      render: (s) => <Money amount={s.reserve_held.amount} className="text-xs text-content-subtle" />,
    },
    {
      key: 'net',
      header: 'Net payout',
      align: 'right',
      render: (s) => <Money amount={s.net_payout.amount} className="text-xs font-medium text-brand-400" />,
    },
    { key: 'status', header: 'Status', render: (s) => <StatusBadge status={s.status} /> },
    {
      key: 'utr',
      header: 'UTR',
      render: (s) =>
        s.utr ? (
          <span className="numeral text-xs text-content-muted">{s.utr}</span>
        ) : (
          <span className="text-2xs text-content-faint">pending</span>
        ),
    },
    {
      key: 'bank',
      header: 'To account',
      render: (s) => (
        <span className="numeral text-xs text-content-subtle">
          {s.bank_account?.bank} ••••{s.bank_account?.last4}
        </span>
      ),
    },
    {
      key: 'date',
      header: 'Date',
      align: 'right',
      render: (s) => (
        <span className="text-xs text-content-subtle">
          {new Date(s.paid_at ?? s.scheduled_for).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: '2-digit',
          })}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <p className="text-xs text-content-muted">
        Settlements run automatically on your agreed cycle. Each row is one bank transfer — match it to your statement
        using the UTR.
      </p>
      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(s) => s.id}
        loading={isLoading}
        empty={
          <EmptyState
            title="No settlements yet"
            description="Your first settlement runs once captured volume ages past your settlement cycle."
          />
        }
      />
    </div>
  );
}
